import {
  IconBrandLinkedin,
  IconDeviceMobileMessage,
  IconFolders,
  IconHelp,
  IconMessageFilled,
  IconMicrophone,
  IconMicrophoneFilled,
  IconSignature,
  IconUserPlus,
} from "@tabler/icons-react";
import {
  Button,
  Divider,
  Indicator,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { Link, useMatches } from "react-router-dom";
import ListingHandle from "../../lists/ListingWithContent/ListingHandle";
import { useListStore } from "../../../../stores/ListStore";
import { IconEmail } from "../../../../assets/icons/IconEmail";
import { useLanguage } from "../../../../stores/LanguageStore";
import Locked from "../../../../components/View/Locked";
import { useMemo } from "react";
import { useProfileStore } from "../../../../stores/UserStore";

const LeftLinks = () => {
  const lang = useLanguage((s) => s.language);
  const matches = useMatches();
  const minimized = useListStore((s) => s.modelMinimized);
  const setMinimized = useListStore((s) => s.setModelMinimized);

  const elements = useMemo(() => {
    let result = {
      email: {
        label: "Email",
        color: "secondary",
        items: [
          {
            label: "Emails",
            link: "/models/email/",
            icon: <IconEmail />,
          },
          {
            label: "Signatures",
            link: "/models/email-signature",
            icon: <IconSignature />,
          },
        ],
      },
      linkedin: {
        label: "LinkedIn",
        color: "blue",
        items: [
          {
            label: "Messages",
            link: "/models/linkedin-message",
            icon: <IconMessageFilled size={20} />,
          },
          {
            label: "Invitations",
            link: "/models/linkedin-invitation",
            icon: <IconUserPlus size={20} />,
          },
          {
            label: "Message vocal",
            link: "/models/linkedin-voice-message",
            icon: <IconMicrophoneFilled size={20} />,
            isNew: true,
          },
        ],
      },
      sms: {
        label: "SMS",
        color: "green",
        items: [
          {
            label: "Messages",
            link: "/models/sms",
            icon: <IconDeviceMobileMessage />,
          },
        ],
      },
      smv: {
        label: lang.model_smv.browser_tab_title,
        color: "green",
        tooltip: lang.header.navigations.models.smv_tooltip,
        items: [
          {
            label: "Messages",
            link: "/models/smv",
            icon: <IconMicrophone />,
          },
        ],
      },
    };

    return result;
  }, [lang.model_smv.browser_tab_title]);

  const handleClick = ({ isRight }) => {
    setMinimized(!minimized);
  };

  return (
    <Paper
      component={Stack}
      w={minimized ? 75 : 250}
      px={minimized ? "sm" : "lg"}
      gap={"md"}
      shadow="lg"
      py={16}
      ml={16}
      pos={"relative"}
      radius={0}
      style={{
        transition: "all ease .3s",
        borderRadius: 12,
      }}
      h={minimized ? 600 : 768}
      justify="center"
    >
      <Stack gap={"lg"}>
        {minimized
          ? Object.keys(elements).map((key) => {
              let group = elements[key];

              return (
                <Stack key={key} gap={"sm"}>
                  {group.items.map((item) => {
                    let active = matches?.find((x) =>
                      x.pathname.includes(`${item.link}`),
                    );

                    return (
                      <Locked
                        disableLock={true}
                        buttonProps={{ size: "sm", radius: "sm" }}
                        top={4}
                        right={-8}
                        key={item?.link}
                        iconOnly
                        starProps={{ size: "sm" }}
                        tooltipProps={{ position: "right" }}
                      >
                        {(isLocked) => (
                          <Tooltip
                            label={item.label}
                            position="right"
                            key={item.link}
                            disabled={isLocked}
                          >
                            <Button
                              component={Link}
                              to={item.link}
                              key={item.link}
                              color={group.color}
                              radius={"md"}
                              h={54}
                              w={isLocked ? 52 : undefined}
                              className="no-shadow"
                              leftSection={
                                <Indicator
                                  color="#ff7b9c"
                                  offset={4}
                                  position="top-start"
                                  size={16}
                                  label={"new"}
                                  styles={{ indicator: { fontSize: 6 } }}
                                  disabled={!item?.isNew}
                                >
                                  <ThemeIcon
                                    variant="filled"
                                    size={"lg"}
                                    color={isLocked ? "gray" : group.color}
                                    radius={"md"}
                                  >
                                    {item.icon}
                                  </ThemeIcon>
                                </Indicator>
                              }
                              size="md"
                              variant={active ? "filled" : "subtle"}
                              justify="start"
                              pl={8}
                            >
                              {item.label}
                            </Button>
                          </Tooltip>
                        )}
                      </Locked>
                    );
                  })}
                </Stack>
              );
            })
          : Object.keys(elements).map((key) => {
              let group = elements[key];

              return (
                <Stack key={key} gap={"sm"}>
                  <Text fz={16} fw={600}>
                    {group.label}

                    {group.tooltip && (
                      <Tooltip label={group.tooltip} position="right">
                        <ThemeIcon>
                          <IconHelp size={18} />
                        </ThemeIcon>
                      </Tooltip>
                    )}
                  </Text>

                  {group.items.map((item, itemIndex) => {
                    let active = matches?.find((x) =>
                      x.pathname.includes(`${item.link}`),
                    );

                    return (
                      <Locked
                        disableLock={true}
                        buttonProps={{
                          size: "xs",
                          display: itemIndex === 1 ? "none" : undefined,
                        }}
                        top={-26}
                        right={0}
                        key={item?.link}
                        withStar
                        starProps={{ size: "sm" }}
                        tooltipProps={{ position: "right" }}
                      >
                        {(isLocked) => (
                          <Button
                            component={Link}
                            to={item.link}
                            key={item.link}
                            color={group.color}
                            radius={"md"}
                            w={"100%"}
                            h={54}
                            className="no-shadow"
                            leftSection={
                              <Indicator
                                color="#ff7b9c"
                                offset={4}
                                position="top-start"
                                size={16}
                                label={"new"}
                                styles={{ indicator: { fontSize: 6 } }}
                                disabled={!item?.isNew}
                              >
                                <ThemeIcon
                                  variant="filled"
                                  size={"lg"}
                                  color={group.color}
                                  radius={"md"}
                                >
                                  {item.icon}
                                </ThemeIcon>
                              </Indicator>
                            }
                            size="md"
                            variant={active ? "filled" : "subtle"}
                            justify="start"
                            pl={8}
                          >
                            {item.label}
                          </Button>
                        )}
                      </Locked>
                    );
                  })}
                </Stack>
              );
            })}
      </Stack>
      <Divider mt={0} />
      {minimized ? (
        <Tooltip position="right" label={lang.prospects.my_folders}>
          <Button
            color={"primary"}
            radius={"md"}
            h={54}
            className="no-shadow"
            leftSection={
              <ThemeIcon
                variant="light"
                size={"lg"}
                color={"primary"}
                radius={"md"}
              >
                <IconFolders />
              </ThemeIcon>
            }
            size="md"
            variant={"subtle"}
            justify="start"
            pl={8}
            component={Link}
            to={"/models/folder"}
          >
            {lang.prospects.my_folders}
          </Button>
        </Tooltip>
      ) : (
        <Button
          leftSection={<IconFolders size={18} />}
          mt={0}
          variant="light"
          className="to-blue"
          component={Link}
          to={"/models/folder"}
          size="md"
        >
          {lang.prospects.my_folders}
        </Button>
      )}

      <ListingHandle
        isMinimized={minimized}
        hidden={minimized}
        onClick={handleClick}
        bg="none"
      />
    </Paper>
  );
};

export default LeftLinks;
